import { useState } from "react";
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Add, Remove, RestartAlt } from "@mui/icons-material";
import { pdfjs, Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { V2_COLORS } from "../constants/colors";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface InvoicePdfViewerProps {
  url: string;
  fileName?: string;
  /** Hide the "Uploaded Invoice" heading (e.g. when inside a modal with its own header). */
  showHeader?: boolean;
  /** Let the viewer fill its parent height instead of capping at 70vh. */
  fillHeight?: boolean;
}

const DEFAULT_SCALE = 1.2;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;

function getFileType(fileName?: string): "pdf" | "image" {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  return ext === "pdf" ? "pdf" : "image";
}

export default function InvoicePdfViewer({ url, fileName, showHeader = true, fillHeight = false }: InvoicePdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState(DEFAULT_SCALE);

  const fileType = getFileType(fileName);

  const handleZoomIn = () => setScale((s) => Math.min(s + SCALE_STEP, MAX_SCALE));
  const handleZoomOut = () => setScale((s) => Math.max(s - SCALE_STEP, MIN_SCALE));
  const handleResetZoom = () => setScale(DEFAULT_SCALE);

  return (
    <Box sx={fillHeight ? { height: "100%", display: "flex", flexDirection: "column" } : undefined}>
      {showHeader && (
        <Typography variant="h4" mb={1.5}>
          Uploaded Invoice
        </Typography>
      )}
      <Box position="relative" sx={{ bgcolor: "background.paper", borderRadius: "8px", overflow: "hidden", ...(fillHeight && { flex: 1, display: "flex", flexDirection: "column" }) }}>
        {/* Zoom controls */}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            px: 1,
            py: 0.5,
            bgcolor: "#fff",
            borderRadius: "6px",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            alignItems: "center",
          }}
        >
          <Tooltip title="Zoom Out">
            <span>
              <IconButton onClick={handleZoomOut} disabled={scale <= MIN_SCALE} size="small" sx={zoomBtnSx}>
                <Remove fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ minWidth: 48, textAlign: "center", fontSize: "13px", fontWeight: 500 }}>
            {Math.round(scale * 100)}%
          </Box>
          <Tooltip title="Zoom In">
            <span>
              <IconButton onClick={handleZoomIn} disabled={scale >= MAX_SCALE} size="small" sx={zoomBtnSx}>
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset Zoom">
            <IconButton onClick={handleResetZoom} size="small" sx={zoomBtnSx}>
              <RestartAlt fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Content area */}
        <Box
          sx={{
            ...(fillHeight ? { flex: 1 } : { maxHeight: "70vh" }),
            overflowX: "auto",
            overflowY: "auto",
            textAlign: "center",
            bgcolor: "#F2F2F2",
            borderRadius: "8px",
          }}
        >
          {fileType === "pdf" ? (
            <Box display="inline-block">
              <Document
                file={url}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={
                  <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress />
                  </Box>
                }
                error={
                  <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                    <Typography color="error">Failed to load PDF</Typography>
                  </Box>
                }
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={`page_${i + 1}`}
                    pageNumber={i + 1}
                    scale={scale}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
              </Document>
            </Box>
          ) : (
            <Box display="inline-block" width="100%">
              <img
                src={url}
                alt={fileName || "Invoice"}
                loading="lazy"
                style={{
                  maxWidth: "100%",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  transition: "transform 0.2s ease-in-out",
                  display: "block",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const zoomBtnSx = {
  border: `1px solid ${V2_COLORS.border}`,
  borderRadius: "4px",
};
